import Axios from 'axios';
import dayjs from 'dayjs';
import _, { forEach } from 'lodash';
import * as React from 'react';
import { FormattedDate } from 'react-intl';
import { Column, UseTableCellProps, UseTableRowProps } from 'react-table';

import { PredefinedFiltersEnum, SnackBarVariants } from '@app/app.enum';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import CustomDatePicker from '@app/components/shared/custom-date-picker.component';
import Icon from '@app/components/shared/icon.component';
import PredefinedFilters from '@app/components/shared/predefined-filters.component';
import ShipmentDetailComponent from '@app/components/shared/shipment-detail.component';
import Table from '@app/components/shared/table.component';
import * as constants from '@app/constants';
import * as models from '@app/models';
import { initialAdminShipmentComponentState, initialAdminShipmentFilter } from '@app/models';
import Injector from '@app/services';
import { AdminShipmentService } from '@app/services/admin-shipment.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import {
	Button,
	Card,
	CardContent,
	Checkbox,
	Collapse,
	FormControl,
	Grid,
	Hidden,
	IconButton,
	ListItemText,
	MenuItem,
	TextField,
	Tooltip,
	Chip,
	Typography,
} from '@material-ui/core';
import CustomButton from '@app/components/shared/custom-button.component';
import { ButtonTypeEnum } from '@app/app.enum';
import CustomTooltip from '@app/components/shared/custom-tooltip.component';

const intlService: IntlService = Injector.get(IntlService);
const adminShipmentService: AdminShipmentService = Injector.get(AdminShipmentService);
const sharedService: SharedService = Injector.get(SharedService);

const RowContentComponent = (props: UseTableRowProps<models.IShipment>) => {
	return <ShipmentDetailComponent shipment={props.original} />;
};

type FilterType = 'startDate' | 'endDate' | 'customerVisibleAccountNumber' | 'trackingNumber';

class AdminShipmentComponent extends React.Component<
	models.IAdminShipmentComponentProps,
	models.IAdminShipmentComponentState
> {
	private prevFiltersInfo: models.IAdminPreviousFilters;
	private shipmentFilter: models.IAdminShipmentFilters = initialAdminShipmentFilter();

	constructor(props: models.IAdminShipmentComponentProps) {
		super(props);
		this.state = initialAdminShipmentComponentState();
	}

	public async componentDidMount() {
		try {
			this.shipmentFilter = (await adminShipmentService.getAdminShipmentFilter()) || initialAdminShipmentFilter();

			this.prevFiltersInfo = {
				selectedCarrierSCACFilterValues: [...this.shipmentFilter.scac],
				selectedCarrierCodeFilterValues: [...this.shipmentFilter.code],
				selectedCarrierProviderFilterValues: [...this.shipmentFilter.provider],
				selectedPartnerFilterValues: [...this.shipmentFilter.partnerVisibleAccountNum],
				selectedShipmentManifestStatusFilterValues: [...this.shipmentFilter.shipmentManifestStatus],
				selectedShipmentRatingFilterValues: [...this.shipmentFilter.shipmentRatingType],
				disabledCheckBox: { ...this.state.disabledCheckBox },
				selectAllCarrierProviderSelection: this.state.selectAllCarrierProviderSelection,
				selectAllCarrierCodeSelection: this.state.selectAllCarrierCodeSelection,
				selectAllCarrierSCACSelection: this.state.selectAllCarrierSCACSelection,
				selectAllPartnerSelection: this.state.selectAllPartnerSelection,
				selectAllShipmentManifestStatusSelection: this.state.selectAllShipmentManifestStatusSelection,
				selectAllShipmentRatingSelection: this.state.selectAllShipmentRatingSelection,
			};
			this.setState({
				selectedCarrierSCACFilterValues: [...this.shipmentFilter.scac],
				selectedCarrierCodeFilterValues: [...this.shipmentFilter.code],
				selectedCarrierProviderFilterValues: [...this.shipmentFilter.provider],
				selectedPartnerFilterValues: [...this.shipmentFilter.partnerVisibleAccountNum],
				selectedShipmentManifestStatusFilterValues: [...this.shipmentFilter.shipmentManifestStatus],
				selectedShipmentRatingFilterValues: [...this.shipmentFilter.shipmentRatingType],
			});
		} catch (error) {
			if (!Axios.isCancel(error)) {
				this.props.openSnackBar(sharedService.parseError(error.data), SnackBarVariants.Error);
			}
		}
	}

	public componentDidUpdate(
		prevProps: models.IAdminShipmentComponentProps,
		prevState: models.IAdminShipmentComponentState
	) {
		if (this.shouldFetchData(prevState)) {
			this.loadData();
		}
	}

	public render() {
		return (
			<React.Fragment>
				<Grid container={true} spacing={2}>
					<Grid item={true} lg={12} md={12} sm={12} xs={12}>
						<Card>
							<CardContent>
								{this.getFilterRow()}
								<Hidden only={['md', 'sm', 'xs']}>
									{/*  admin shipment table*/}
									<Grid container={true}>
										<Table<models.IShipment>
											columns={this.getGridColumns()}
											data={this.state.results}
											totalPages={this.state.totalPages}
											dataLoading={this.state.dataLoading}
											currentPage={this.state.currentPage}
											canExpand={true}
											rowContent={(props: UseTableRowProps<models.IShipment>) => RowContentComponent(props)}
											onPageChange={(e: object, page: number) => this.setState({ currentPage: page })}
											pageSize={constants.pageSize}
											totalRecords={this.state.totalResults}
										/>
									</Grid>
								</Hidden>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</React.Fragment>
		);
	}

	public filterHelperFunc(
		event: React.ChangeEvent<any>,
		rootFilterList: string[],
		stateKeyName: keyof models.IAdminShipmentComponentState,
		selectAllStateName: keyof models.IAdminShipmentComponentState
	) {
		switch (event.target.value.includes(intlService.GetFormattedMessage('app.shipment.done'))) {
			case true:
				this.prevFiltersInfo = {
					...this.prevFiltersInfo,
					[stateKeyName]: this.state[stateKeyName],
					disabledCheckBox: { ...this.state.disabledCheckBox },
					selectAllCarrierProviderSelection: this.state.selectAllCarrierProviderSelection,
					selectAllCarrierSCACSelection: this.state.selectAllCarrierSCACSelection,
					selectAllCarrierCodeSelection: this.state.selectAllCarrierCodeSelection,
				};
				this.performActionOnDone(stateKeyName);
				break;
			case false:
				this.applyFiltersCorrectly(event.target.value, rootFilterList, stateKeyName, selectAllStateName);
				break;
		}
	}

	private async performActionOnDone(stateKeyName: string) {
		this.setState({
			isOpenCarrierSCACFilter: false,
			isOpenCarrierCodeFilter: false,
			isOpenCarrierProviderFilter: false,
			isOpenPartnerFilter: false,
			isOpenShipmentManifestStatusFilter: false,
			isOpenShipmentRatingFilter: false,
			isFilterApplied: true,
			currentPage: 1,
		});
		if (stateKeyName === 'selectedCarrierSCACFilterValues') {
			await this.updateCarrierCodeFiltersAfterSCACUpdate();
		}
	}

	private applyFiltersCorrectly(
		value: string[],
		rootFilterList: string[],
		stateKeyName: keyof models.IAdminShipmentComponentState,
		selectAllStateName: keyof models.IAdminShipmentComponentState
	) {
		const isSelectAllApplied = value.includes(intlService.GetFormattedMessage('app.filter-select-all'));

		const doneState: boolean = isSelectAllApplied && !this.state[selectAllStateName];
		value = value.filter((element) => element !== intlService.GetFormattedMessage('app.filter-select-all'));
		let disabled = {};
		forEach(rootFilterList, (element) => {
			if (this.state.disabledCheckBox[element]) {
				disabled = { ...this.state.disabledCheckBox, [element]: false };
			}
		});

		disabled =
			value.length === 1
				? { ...this.state.disabledCheckBox, [value[0]]: !this.state.disabledCheckBox[value[0]] }
				: { ...this.state.disabledCheckBox, ...disabled };

		switch (doneState) {
			case true:
				value = rootFilterList;
				disabled = {
					...this.state.disabledCheckBox,
					[value[0]]: false,
				};
				break;
			case false:
				// Scenario:
				// 1.) If user checked select-all again for deselecting all filters.
				// 2.) If user de-selects the only item present inside the list.
				if (isSelectAllApplied || !value.length) {
					value = isSelectAllApplied ? value : rootFilterList;
					const temp = value[0];
					value = [];
					value.push(temp);
					disabled = {
						...this.state.disabledCheckBox,
						[value[0]]: !this.state.disabledCheckBox[value[0]],
					};
				}

				break;
		}

		// Scenarios:
		// 1.) If rootFilterList contains more than one item and both (value and rootFilterList) are of same length then SelectAll checkbox should be ticked.
		// 2.) If they they are of different lengths then SelectAll checkbox should not be ticked.
		// 3.) If rootFilterList contain only single item then SelectAll checkbox should be ticked as per the value of "doneState".
		const SelectAllCheckBoxValue =
			value.length === rootFilterList.length && rootFilterList.length > 1 ? true : doneState;

		this.setState({
			...this.state,
			[stateKeyName]: value,
			[selectAllStateName]: SelectAllCheckBoxValue,
			disabledCheckBox: disabled,
		});
	}

	private updateCarrierCodeFiltersAfterSCACUpdate = async () => {
		this.shipmentFilter.code = (
			await adminShipmentService.getAdminShipmentFilter(this.state.selectedCarrierSCACFilterValues)
		).code;
		this.prevFiltersInfo = {
			selectedCarrierCodeFilterValues: [...this.shipmentFilter.code],
		};

		this.setState({
			selectAllCarrierCodeSelection: false,
		});

		this.applyFiltersCorrectly(
			[...this.shipmentFilter.code, intlService.GetFormattedMessage('app.filter-select-all')],
			[...this.shipmentFilter.code],
			'selectedCarrierCodeFilterValues',
			'selectAllCarrierCodeSelection'
		);
	};
	private getGridColumns = (): Array<Column<models.IShipment>> => {
		return [
			{
				accessor: 'transactionId',
				Header: intlService.GetFormattedMessage('app.shipment.transactionId'),
				width: 190,
				disableSortBy: true,
			},
			{
				accessor: 'createdDate',
				Header: intlService.GetFormattedMessage('app.label.date-table-header'),
				Cell: (tableData: UseTableCellProps<models.IShipment>) => (
					<>
						<FormattedDate value={sharedService.getNewDateIfNotExist(tableData.value)} />
					</>
				),
				width: 100,
				disableSortBy: true,
			},
			{
				accessor: 'carrierCode',
				Header: intlService.GetFormattedMessage('app.carrier.carrier-code-label'),
				Cell: (tableData: UseTableCellProps<models.IShipment>) => <>{tableData.row.values.carrierCode}</>,
				width: 130,
				disableSortBy: true,
			},
			{
				accessor: 'tracking',
				Header: intlService.GetFormattedMessage('app.shipment.tracking-number'),
				Cell: (tableData: UseTableCellProps<models.IShipment>) => (
					<>
						{tableData.value.trackingNumber}
						{tableData.row.original?.refundStatus && (
							<Chip
								label={tableData.row.original.refundStatus}
								color='default'
								size='small'
								className='ml-1 refund-chip'
							/>
						)}
					</>
				),
				width: 200,
				disableSortBy: true,
			},
			{
				accessor: 'rates',
				Header: intlService.GetFormattedMessage('app.shipment.rate-label') + '($)',
				Cell: (tableData: UseTableCellProps<models.IShipment>) => (
					<>
						{tableData.row.values.rates && tableData.row.values.rates[0]
							? tableData.row.values.rates[0].totalFee.toString()
							: intlService.GetFormattedMessage('app.shipment.rate-column-norated')}
					</>
				),
				width: 100,
				disableSortBy: true,
			},
			{
				accessor: 'shipmentManifestStatus',
				Header: intlService.GetFormattedMessage('app.shipment.manifest-status-label'),
				Cell: (tableData: UseTableCellProps<models.IShipment>) => (
					<>
						{tableData.row.values.shipmentManifestStatus}

						{/* Manifest status is properly updated only for native carriers. So if it's NOT a native carrier then show an warning about it. */}
						{!constants.NativeCarriers.includes(tableData.row.values.carrierCode) && (
							<CustomTooltip
								items={[
									{
										value: intlService.GetFormattedMessage('app.shipment.non-native-manifest-status-warning'),
									},
								]}
								applyMinWidth={false}
								className='ml-1'
							>
								<Typography component='span'>
									<Icon name='info-circle' />
								</Typography>
							</CustomTooltip>
						)}
					</>
				),
				width: 100,
				disableSortBy: true,
			},
		];
	};

	private getFilterRow() {
		return (
			<>
				<Grid container={true} spacing={2}>
					<Grid container={true} item={true} lg={9} md={12} sm={12} xs={12}>
						<Grid container={true} justifyContent='flex-start' spacing={2}>
							<Grid item={true} lg={3} md={3} sm={12} xs={12}>
								<PredefinedFilters
									startDate={this.state.startDate}
									endDate={this.state.endDate}
									onDateChange={this.callAfterCancellingActiveRequests(this.loadDataForPredefinedFilters)}
								/>
							</Grid>
							<Grid item={true} lg={3} md={3} sm={12} xs={12}>
								<CustomDatePicker
									label={intlService.GetFormattedMessage('app.balance-history.start-date-label')}
									maxDate={this.state.endDate}
									disabled={this.state.disableDatePickerFilter}
									value={this.state.startDate}
									onChange={(date: dayjs.Dayjs) => {
										this.setStateAfterCancellingActiveRequests('startDate', date.toDate());
									}}
								/>
							</Grid>
							<Grid item={true} lg={3} md={3} sm={12} xs={12}>
								<CustomDatePicker
									label={intlService.GetFormattedMessage('app.balance-history.end-date-label')}
									minDate={this.state.startDate}
									disabled={this.state.disableDatePickerFilter}
									value={this.state.endDate}
									onChange={(date: dayjs.Dayjs) => {
										this.setStateAfterCancellingActiveRequests('endDate', date.toDate());
									}}
								/>
							</Grid>

							{/* Tracking number filter */}
							<Grid item={true} lg={3} md={4} sm={12} xs={12}>
								<TextField
									label={intlService.GetFormattedMessage('app.shipment.tracking-number')}
									placeholder={intlService.GetFormattedMessage('app.shipment.min-tracking-number-placeholder')}
									fullWidth={true}
									value={this.state.trackingNumber}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
										this.setState({
											trackingNumber: e.target.value,
										});
									}}
									variant='outlined'
									error={this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3}
									helperText={
										this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3
											? intlService.GetFormattedMessage('app.shipment.min-tracking-number-helper-text')
											: null
									}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item={true} lg={3} md={12} sm={12} xs={12}>
						<Hidden only={['sm', 'xs', 'md']}>
							<Grid container={true} alignItems='center' style={{ height: '100%' }}>
								<Grid item={true} lg={9} md={9} sm={12} xs={12}>
									{/* Filter Button */}
									<Tooltip title={intlService.GetFormattedMessage('app.balance-history.more-filters')}>
										<IconButton
											onClick={() => {
												this.setState({
													filterPanelExpanded: !this.state.filterPanelExpanded,
												});
											}}
										>
											<Icon name='bars-horizontal-funnel-down' style={{ transform: 'translateY(-2px)' }} />
										</IconButton>
									</Tooltip>

									{/* Export Button */}
									<Tooltip title={intlService.GetFormattedMessage('app.common.export')}>
										<IconButton
											onClick={() => {
												this.exportAdminShipmentData();
											}}
										>
											<Icon name='tray-arrow-down' />
										</IconButton>
									</Tooltip>
									{/* Search Button */}
									<CustomButton
										fullWidth={false}
										type={ButtonTypeEnum.Button}
										buttonText={intlService.GetFormattedMessage('app.common.search')}
										onClick={() => {
											if (this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3) {
												return;
											}
											this.loadData();
										}}
									/>
								</Grid>
							</Grid>
						</Hidden>
						<Hidden only={['xl', 'lg']}>
							<Grid container={true} alignItems='center' spacing={3} style={{ height: '100%' }}>
								{/* Filter Button */}
								<Grid item={true}>
									<Button
										variant='outlined'
										color='primary'
										onClick={() => {
											this.setState({
												filterPanelExpanded: !this.state.filterPanelExpanded,
											});
										}}
									>
										<Icon name='bars-horizontal-funnel-down' style={{ transform: 'translateY(-2px)' }} />
										<span className='pl-1'>{intlService.GetFormattedMessage('app.balance-history.more-filters')}</span>
									</Button>
								</Grid>

								{/* Export Button */}
								<Grid item={true}>
									<Button
										variant='outlined'
										color='primary'
										onClick={() => {
											this.exportAdminShipmentData();
										}}
									>
										<Icon name='tray-arrow-down' />
										<span className='pl-1'>{intlService.GetFormattedMessage('app.common.export')}</span>
									</Button>
								</Grid>

								{/* Search Button */}
								<Grid item={true}>
									<CustomButton
										fullWidth={false}
										type={ButtonTypeEnum.Button}
										buttonText={intlService.GetFormattedMessage('app.common.search')}
										onClick={() => {
											if (this.state.trackingNumber.length > 0 && this.state.trackingNumber.length < 3) {
												return;
											}
											this.loadData();
										}}
									/>
								</Grid>
							</Grid>
						</Hidden>
					</Grid>
					<Grid item={true} lg={9} md={12} sm={12} xs={12}>
						<Collapse in={this.state.filterPanelExpanded}>
							<Grid container={true} spacing={2}>
								{/* Carrier Scac  filter*/}
								<Grid item={true} lg={3} md={3} sm={12} xs={12}>
									<FormControl fullWidth={true}>
										<TextField
											label={intlService.GetFormattedMessage('app.carrier-scac')}
											select={true}
											value={this.state.selectedCarrierSCACFilterValues}
											onChange={(e: React.ChangeEvent) => {
												this.filterHelperFunc(
													e,
													[...this.shipmentFilter.scac],
													'selectedCarrierSCACFilterValues',
													'selectAllCarrierSCACSelection'
												);
											}}
											inputProps={{
												'data-testid': 'carrier-scac',
											}}
											SelectProps={{
												open: this.state.isOpenCarrierSCACFilter,
												multiple: true,
												onClose: async () => {
													this.setState({
														...this.state,
														...this.prevFiltersInfo,
														isOpenCarrierSCACFilter: false,
													});
												},
												onOpen: () =>
													this.setState({
														isOpenCarrierSCACFilter: !this.state.isOpenCarrierSCACFilter,
													}),
												renderValue: (selected) => {
													return this.state.selectAllCarrierSCACSelection
														? intlService.GetFormattedMessage('app.shipment.all-label')
														: (selected as string[]).map((x) => x).join(', ');
												},
											}}
											variant='outlined'
										>
											<MenuItem
												key={intlService.GetFormattedMessage('app.filter-select-all')}
												value={intlService.GetFormattedMessage('app.filter-select-all')}
											>
												<Checkbox checked={this.state.selectAllCarrierSCACSelection} />
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-select-all')} />
											</MenuItem>
											{this.shipmentFilter.scac.map((x) => (
												<MenuItem key={x} value={x} disabled={this.state.disabledCheckBox[x]}>
													<Checkbox checked={this.state.selectedCarrierSCACFilterValues.includes(x)} />
													<ListItemText primary={x} />
												</MenuItem>
											))}
											<MenuItem className='text-center' value={intlService.GetFormattedMessage('app.filter-done')}>
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-done')} />
											</MenuItem>
										</TextField>
									</FormControl>
								</Grid>

								{/* Carrier Code filter*/}
								<Grid item={true} lg={3} md={4} sm={12} xs={12}>
									<FormControl fullWidth={true}>
										<TextField
											inputProps={{
												'data-testid': 'carrier-code',
											}}
											label={intlService.GetFormattedMessage('app.carrier.carrier-code-label')}
											select={true}
											value={this.state.selectedCarrierCodeFilterValues}
											onChange={(e: React.ChangeEvent) => {
												this.filterHelperFunc(
													e,
													[...this.shipmentFilter.code],
													'selectedCarrierCodeFilterValues',
													'selectAllCarrierCodeSelection'
												);
											}}
											SelectProps={{
												open: this.state.isOpenCarrierCodeFilter,
												multiple: true,
												onClose: async () => {
													this.setState({
														...this.state,
														...this.prevFiltersInfo,
														isOpenCarrierCodeFilter: false,
													});
												},
												onOpen: () =>
													this.setState({
														isOpenCarrierCodeFilter: !this.state.isOpenCarrierCodeFilter,
													}),
												renderValue: (selected) => {
													return this.state.selectAllCarrierCodeSelection
														? intlService.GetFormattedMessage('app.shipment.all-label')
														: (selected as string[]).map((x) => x).join(', ');
												},
											}}
											variant='outlined'
										>
											<MenuItem
												key={intlService.GetFormattedMessage('app.filter-select-all')}
												value={intlService.GetFormattedMessage('app.filter-select-all')}
											>
												<Checkbox checked={this.state.selectAllCarrierCodeSelection} />
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-select-all')} />
											</MenuItem>
											{this.shipmentFilter.code.map((x) => (
												<MenuItem key={x} value={x} disabled={this.state.disabledCheckBox[x]}>
													<Checkbox checked={this.state.selectedCarrierCodeFilterValues.includes(x)} />
													<ListItemText primary={x} />
												</MenuItem>
											))}
											<MenuItem
												className='text-center'
												key={intlService.GetFormattedMessage('app.filter-done')}
												value={intlService.GetFormattedMessage('app.filter-done')}
											>
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-done')} />
											</MenuItem>
										</TextField>
									</FormControl>
								</Grid>

								{/* Carrier Provider filter*/}

								<Grid item={true} lg={3} md={4} sm={12} xs={12}>
									<FormControl fullWidth={true}>
										<TextField
											inputProps={{
												'data-testid': 'carrier-provide',
											}}
											label={intlService.GetFormattedMessage('app.carrier.carrier-provider-label')}
											select={true}
											value={this.state.selectedCarrierProviderFilterValues}
											onChange={(e: React.ChangeEvent) => {
												this.filterHelperFunc(
													e,
													[...this.shipmentFilter.provider],
													'selectedCarrierProviderFilterValues',
													'selectAllCarrierProviderSelection'
												);
											}}
											SelectProps={{
												open: this.state.isOpenCarrierProviderFilter,
												multiple: true,
												onClose: async () => {
													this.setState({
														...this.state,
														...this.prevFiltersInfo,
														isOpenCarrierProviderFilter: false,
													});
												},
												onOpen: () =>
													this.setState({
														isOpenCarrierProviderFilter: !this.state.isOpenCarrierProviderFilter,
													}),
												renderValue: (selected) => {
													return this.state.selectAllCarrierProviderSelection
														? intlService.GetFormattedMessage('app.shipment.all-label')
														: (selected as string[]).map((x) => x).join(', ');
												},
											}}
											variant='outlined'
										>
											<MenuItem
												key={intlService.GetFormattedMessage('app.filter-select-all')}
												value={intlService.GetFormattedMessage('app.filter-select-all')}
											>
												<Checkbox checked={this.state.selectAllCarrierProviderSelection} />
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-select-all')} />
											</MenuItem>
											{this.shipmentFilter.provider.map((x) => (
												<MenuItem key={x} value={x} disabled={this.state.disabledCheckBox[x]}>
													<Checkbox checked={this.state.selectedCarrierProviderFilterValues.includes(x)} />
													<ListItemText primary={x} />
												</MenuItem>
											))}
											<MenuItem
												className='text-center'
												key={intlService.GetFormattedMessage('app.filter-done')}
												value={intlService.GetFormattedMessage('app.filter-done')}
											>
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-done')} />
											</MenuItem>
										</TextField>
									</FormControl>
								</Grid>

								{/* Manifest status filter*/}
								<Grid item={true} lg={3} md={4} sm={12} xs={12}>
									<FormControl fullWidth={true}>
										<TextField
											label={intlService.GetFormattedMessage('app.shipment.manifest-status-label')}
											select={true}
											value={this.state.selectedShipmentManifestStatusFilterValues}
											onChange={(e: React.ChangeEvent) => {
												this.filterHelperFunc(
													e,
													[...this.shipmentFilter.shipmentManifestStatus],
													'selectedShipmentManifestStatusFilterValues',
													'selectAllShipmentManifestStatusSelection'
												);
											}}
											inputProps={{
												'data-testid': 'manifest-status',
											}}
											SelectProps={{
												open: this.state.isOpenShipmentManifestStatusFilter,
												multiple: true,
												onClose: async () => {
													this.setState({
														...this.state,
														...this.prevFiltersInfo,
														isOpenShipmentManifestStatusFilter: false,
													});
												},
												onOpen: () =>
													this.setState({
														isOpenShipmentManifestStatusFilter: !this.state.isOpenShipmentManifestStatusFilter,
													}),
												renderValue: (selected) => {
													return this.state.selectAllShipmentManifestStatusSelection
														? intlService.GetFormattedMessage('app.shipment.all-label')
														: (selected as string[]).map((x) => x).join(', ');
												},
											}}
											variant='outlined'
										>
											<MenuItem
												key={intlService.GetFormattedMessage('app.filter-select-all')}
												value={intlService.GetFormattedMessage('app.filter-select-all')}
											>
												<Checkbox checked={this.state.selectAllShipmentManifestStatusSelection} />
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-select-all')} />
											</MenuItem>
											{this.shipmentFilter.shipmentManifestStatus.map((x) => (
												<MenuItem key={x} value={x} disabled={this.state.disabledCheckBox[x]}>
													<Checkbox checked={this.state.selectedShipmentManifestStatusFilterValues.includes(x)} />
													<ListItemText primary={x} />
												</MenuItem>
											))}
											<MenuItem className='text-center' value={intlService.GetFormattedMessage('app.filter-done')}>
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-done')} />
											</MenuItem>
										</TextField>
									</FormControl>
								</Grid>

								{/* Rating type filter*/}
								<Grid item={true} lg={3} md={4} sm={12} xs={12}>
									<FormControl fullWidth={true}>
										<TextField
											label={intlService.GetFormattedMessage('app.shipment.filter.shipment-rating')}
											select={true}
											value={this.state.selectedShipmentRatingFilterValues}
											onChange={(e: React.ChangeEvent) => {
												this.filterHelperFunc(
													e,
													[...this.shipmentFilter.shipmentRatingType],
													'selectedShipmentRatingFilterValues',
													'selectAllShipmentRatingSelection'
												);
											}}
											inputProps={{
												'data-testid': 'rating-type',
											}}
											SelectProps={{
												open: this.state.isOpenShipmentRatingFilter,
												multiple: true,
												onClose: async () => {
													this.setState({
														...this.state,
														...this.prevFiltersInfo,
														isOpenShipmentRatingFilter: false,
													});
												},
												onOpen: () =>
													this.setState({
														isOpenShipmentRatingFilter: !this.state.isOpenShipmentRatingFilter,
													}),
												renderValue: (selected) => {
													return this.state.selectAllShipmentRatingSelection
														? intlService.GetFormattedMessage('app.shipment.all-label')
														: (selected as string[]).map((x) => x).join(', ');
												},
											}}
											variant='outlined'
										>
											<MenuItem
												key={intlService.GetFormattedMessage('app.filter-select-all')}
												value={intlService.GetFormattedMessage('app.filter-select-all')}
											>
												<Checkbox checked={this.state.selectAllShipmentRatingSelection} />
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-select-all')} />
											</MenuItem>
											{this.shipmentFilter.shipmentRatingType.map((x) => (
												<MenuItem key={x} value={x} disabled={this.state.disabledCheckBox[x]}>
													<Checkbox checked={this.state.selectedShipmentRatingFilterValues.includes(x)} />
													<ListItemText primary={x} />
												</MenuItem>
											))}
											<MenuItem className='text-center' value={intlService.GetFormattedMessage('app.filter-done')}>
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-done')} />
											</MenuItem>
										</TextField>
									</FormControl>
								</Grid>

								{/*  Partner-visible-account-Numner filter*/}

								<Grid item={true} lg={3} md={4} sm={12} xs={12}>
									<FormControl fullWidth={true}>
										<TextField
											inputProps={{
												'data-testid': 'partner-visible-account',
											}}
											label={intlService.GetFormattedMessage('app.account.partner-visible-account-label')}
											select={true}
											value={this.state.selectedPartnerFilterValues}
											onChange={(e: React.ChangeEvent) => {
												this.filterHelperFunc(
													e,
													[...this.shipmentFilter.partnerVisibleAccountNum],
													'selectedPartnerFilterValues',
													'selectAllPartnerSelection'
												);
											}}
											SelectProps={{
												open: this.state.isOpenPartnerFilter,
												multiple: true,
												onClose: async () => {
													this.setState({
														...this.state,
														...this.prevFiltersInfo,
														isOpenPartnerFilter: false,
													});
												},
												onOpen: () =>
													this.setState({
														isOpenPartnerFilter: !this.state.isOpenPartnerFilter,
													}),
												renderValue: (selected) => {
													return this.state.selectAllPartnerSelection
														? intlService.GetFormattedMessage('app.shipment.all-label')
														: (selected as string[]).map((x) => x).join(', ');
												},
											}}
											variant='outlined'
										>
											<MenuItem
												key={intlService.GetFormattedMessage('app.filter-select-all')}
												value={intlService.GetFormattedMessage('app.filter-select-all')}
											>
												<Checkbox checked={this.state.selectAllPartnerSelection} />
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-select-all')} />
											</MenuItem>
											{this.shipmentFilter.partnerVisibleAccountNum.map((x) => (
												<MenuItem key={x} value={x} disabled={this.state.disabledCheckBox[x]}>
													<Checkbox checked={this.state.selectedPartnerFilterValues.includes(x)} />
													<ListItemText primary={x} />
												</MenuItem>
											))}
											<MenuItem
												className='text-center'
												key={intlService.GetFormattedMessage('app.filter-done')}
												value={intlService.GetFormattedMessage('app.filter-done')}
											>
												<ListItemText primary={intlService.GetFormattedMessage('app.filter-done')} />
											</MenuItem>
										</TextField>
									</FormControl>
								</Grid>

								{/*  Company filter*/}

								<Grid item={true} lg={3} md={4} sm={12} xs={12}>
									<FormControl fullWidth={true}>
										<TextField
											inputProps={{
												'data-testid': 'customer-visible-account',
											}}
											label={intlService.GetFormattedMessage('app.account.customer-visible-account-label')}
											value={this.state.customerVisibleAccountNumber}
											onChange={(e) => {
												this.setStateAfterCancellingActiveRequests('customerVisibleAccountNumber', e.target.value);
											}}
											variant='outlined'
										/>
									</FormControl>
								</Grid>
							</Grid>
						</Collapse>
					</Grid>
				</Grid>
			</>
		);
	}

	private async exportAdminShipmentData() {
		try {
			const res = await adminShipmentService.exportAdminShipmentData(
				this.state.startDate,
				this.state.endDate,
				this.state.selectedCarrierSCACFilterValues,
				this.state.selectedCarrierCodeFilterValues,
				this.state.selectedCarrierProviderFilterValues,
				this.state.selectedPartnerFilterValues,
				this.state.customerVisibleAccountNumber,
				this.state.trackingNumber,
				this.props.axiosCancellationToken
			);
			const fileName = sharedService.parseContentDisposition(
				res.request.getResponseHeader(constants.contentDispositionHeader)
			);
			sharedService.downloadFile(res.data, fileName);
		} catch (e) {
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	}

	private loadData = async () => {
		try {
			const {
				currentPage,
				startDate,
				endDate,
				selectedCarrierSCACFilterValues,
				selectedCarrierCodeFilterValues,
				selectedCarrierProviderFilterValues,
				selectedPartnerFilterValues,
				selectedShipmentManifestStatusFilterValues,
				selectedShipmentRatingFilterValues,
				customerVisibleAccountNumber,
				trackingNumber,
			} = this.state;

			let pageToken = sharedService.generatePaginationToken(currentPage - 2);

			if (currentPage === 0) {
				pageToken = undefined;
			}

			this.setState({
				dataLoading: true,
			});
			const shipmentData = await adminShipmentService.GetAdminShipmentData(
				pageToken,
				startDate,
				endDate,
				selectedCarrierSCACFilterValues,
				selectedCarrierCodeFilterValues,
				selectedCarrierProviderFilterValues,
				selectedPartnerFilterValues,
				customerVisibleAccountNumber,
				trackingNumber,
				selectedShipmentManifestStatusFilterValues,
				selectedShipmentRatingFilterValues,
				this.props.axiosCancellationToken
			);
			this.setState({
				...shipmentData,
				dataLoading: false,
				isFilterApplied: false,
			});
		} catch (e) {
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	};

	private loadDataForPredefinedFilters = (startDate: Date, endDate: Date, filterValue: PredefinedFiltersEnum) => {
		this.setState({
			currentPage: 1,
			startDate,
			endDate,
			disableDatePickerFilter: filterValue !== PredefinedFiltersEnum.Custom,
		});
	};

	private shouldFetchData = (prevState: models.IAdminShipmentComponentState): boolean => {
		return prevState.currentPage !== this.state.currentPage && !this.props.isRequestCancelled;
	};

	private setStateAfterCancellingActiveRequests = (key: FilterType, value: any) => {
		const composedFunc = this.callAfterCancellingActiveRequests(() => {
			this.setState((prevState: models.IAdminShipmentComponentState) => ({
				...prevState,
				currentPage: 1,
				[key]: value,
			}));
		});

		composedFunc();
	};

	private callAfterCancellingActiveRequests = (func: (...args: any) => void) => {
		return async (...args: any) => {
			await this.props.cancelActiveRequests();
			func(...args);
		};
	};
}
export default withSnackBar(withAxiosCancellation(AdminShipmentComponent));



// WEBPACK FOOTER //
// ./src/components/admin/shipment/admin-shipment.component.tsx